import { useState, useCallback, useEffect } from "react";
import { api } from "../services/api";
import { track } from "../lib/posthog";

/**
 * Hook global de suscripción.
 * Expone estado de la suscripción del usuario y helpers para mutaciones.
 */
export function useSubscription() {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSubscription = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.subscriptions.get();
      setSubscription(data);
    } catch (e) {
      setError(e.message || "Error al cargar suscripción");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const cancel = useCallback(async () => {
    const data = await api.subscriptions.cancel();
    track("subscription_canceled");
    await fetchSubscription();
    return data;
  }, [fetchSubscription]);

  const reactivate = useCallback(async () => {
    const data = await api.subscriptions.reactivate();
    track("subscription_reactivated");
    await fetchSubscription();
    return data;
  }, [fetchSubscription]);

  const requestDeletion = useCallback(async (reason) => {
    // NO se envía reason a PostHog — puede contener PII
    const data = await api.account.requestDeletion(reason);
    track("account_deletion_requested");
    await fetchSubscription();
    return data;
  }, [fetchSubscription]);

  const cancelDeletion = useCallback(async () => {
    const data = await api.account.cancelDeletion();
    track("account_deletion_canceled");
    await fetchSubscription();
    return data;
  }, [fetchSubscription]);

  /**
   * Activa trial de 15 días sin tarjeta para el plan dado.
   * Solo funciona si trial_used_at === null.
   */
  const startTrial = useCallback(async (plan) => {
    const data = await api.subscriptions.startTrial(plan);
    track("subscription_trial_started", { plan });
    await fetchSubscription();
    return data;
  }, [fetchSubscription]);

  /**
   * Inicia Stripe Checkout. Trackea ANTES del redirect para no perder el evento.
   * Si la API falla, lanza el error (el componente lo captura y muestra toast).
   */
  const goToCheckout = useCallback(async (plan) => {
    const data = await api.subscriptions.checkout(plan);
    track("subscription_checkout_started", { plan });
    window.location.href = data.checkout_url;
  }, []);

  /**
   * Abre el Stripe Billing Portal para gestionar la suscripción activa.
   */
  const goToBillingPortal = useCallback(async () => {
    const data = await api.subscriptions.billingPortal();
    track("subscription_billing_portal_opened");
    window.location.href = data.portal_url;
  }, []);

  return {
    subscription,
    loading,
    error,
    refetch: fetchSubscription,
    cancel,
    reactivate,
    requestDeletion,
    cancelDeletion,
    startTrial,
    goToCheckout,
    goToBillingPortal,
  };
}
